import React, { useState } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Button,
  Typography,
  TablePagination,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Clear as ClearIcon,
  FilterAlt as FilterAltIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from './ScrollableDialog';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';

const tableSx = {
  '& .MuiTableCell-root': {
    borderBottomColor: BRAND.line,
  },
  '& .MuiTableRow-hover:hover': {
    backgroundColor: brandAlpha('accent', 0.04),
  },
  '& .MuiTableRow-root.Mui-selected, & .MuiTableRow-root.Mui-selected:hover': {
    backgroundColor: brandAlpha('accent', 0.08),
  },
  '& .MuiTableCell-body a': {
    color: BRAND.accentText,
  },
};

const isActionsColumn = (column) => (
  column.field === 'actions'
  || column.mobileRole === 'actions'
);

const DataTable = ({
  data = [],
  loading = false,
  error = null,
  columns = [],
  searchable = true,
  searchPlaceholder = '',
  onRefresh = null,
  onRowAction = null,
  actionMenuItems = [],
  pagination = true,
  rowsPerPage = 10,
  emptyMessage = '',
  noResultsMessage = '',
  searchTerm = '',
  onSearchChange = null,
  sortField = '',
  sortDirection = 'asc',
  onSort = null,
  currentPage = 1,
  onPageChange = null,
  totalCount = 0,
  // Filter props
  filterable = true,
  filterOptions = [],
  activeFilters = {},
  onFilterChange = null,
  onClearFilters = null,
  sx = {}
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(activeFilters);

  const handleMenuClick = (event, row, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow({ ...row, index });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleActionClick = (action) => {
    if (onRowAction && selectedRow) {
      onRowAction(action, selectedRow);
    }
    handleMenuClose();
  };

  const handleSort = (field) => {
    if (onSort) {
      onSort(field);
    }
  };

  // Filter handlers
  const handleFilterClick = () => {
    setTempFilters(activeFilters);
    setFilterDialogOpen(true);
  };

  const handleFilterClose = () => {
    setFilterDialogOpen(false);
    setTempFilters(activeFilters);
  };

  const handleFilterApply = () => {
    if (onFilterChange) {
      onFilterChange(tempFilters);
    }
    setFilterDialogOpen(false);
  };

  const handleFilterClear = () => {
    if (onClearFilters) {
      onClearFilters();
    }
    setTempFilters({});
    setFilterDialogOpen(false);
  };

  const handleFilterChange = (filterKey, value) => {
    setTempFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(value => 
    value !== null && value !== undefined && value !== '' && 
    !(Array.isArray(value) && value.length === 0)
  ).length;

  const handlePageChange = (event, newPage) => {
    if (onPageChange) {
      onPageChange(newPage + 1);
    }
  };

  const loadingRowCount = Math.max(3, Math.min(rowsPerPage || 5, 6));
  const visibleColumns = columns.filter((column) => !column.hideOnMobile || !isMobile);
  const primaryColumn = visibleColumns.find((column) => column.mobilePrimary)
    || visibleColumns.find((column) => !isActionsColumn(column));
  const detailColumns = visibleColumns.filter((column) => (
    column !== primaryColumn && !isActionsColumn(column)
  ));
  const actionsColumns = visibleColumns.filter((column) => isActionsColumn(column));
  const hasInlineActions = actionMenuItems.length > 0 || actionsColumns.length > 0;

  const renderCellContent = (column, row, index) => (
    column.render ? column.render(row, index) : row[column.field]
  );

  const renderRowActions = (row, index) => {
    if (!hasInlineActions) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-start' : 'flex-end',
          gap: isMobile ? 0.5 : 1,
          width: '100%',
          minWidth: 0,
        }}
      >
        {actionsColumns.map((column, columnIndex) => (
          <Box
            key={column.field || columnIndex}
            sx={{
              minWidth: 0,
              maxWidth: '100%',
              flex: '1 1 auto',
              width: '100%',
              display: 'flex',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
            }}
          >
            {renderCellContent(column, row, index)}
          </Box>
        ))}
        {actionMenuItems.map((action, actionIndex) => (
          <Tooltip key={actionIndex} title={action.tooltip}>
            <IconButton
              onClick={(e) => action.onClick ? action.onClick(row, index) : handleMenuClick(e, row, index)}
              size="small"
              sx={{ color: action.color || BRAND.textMuted }}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        ))}
        {actionMenuItems.length > 1 && (
          <Tooltip title={t('common.moreOptions')}>
            <IconButton
              onClick={(e) => handleMenuClick(e, row, index)}
              size="small"
              sx={{ color: BRAND.textMuted }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderLoadingCell = (column, rowIndex, columnIndex) => {
    if (column.field === 'actions' || column.align === 'right') {
      return (
        <Box sx={{ display: 'flex', justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start', gap: 1 }}>
          <Skeleton variant="circular" animation="wave" width={28} height={28} />
          <Skeleton variant="circular" animation="wave" width={28} height={28} />
        </Box>
      );
    }

    if (columnIndex === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="rounded" animation="wave" width={28} height={28} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" animation="wave" width="62%" height={26} />
          </Box>
        </Box>
      );
    }

    const widths = ['74%', '58%', '68%', '52%'];

    return (
      <Skeleton
        variant="text"
        animation="wave"
        width={widths[(rowIndex + columnIndex) % widths.length]}
        height={26}
      />
    );
  };

  const renderMobileCards = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
          {Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
            <Box
              key={`loading-card-${rowIndex}`}
              sx={{
                border: `1px solid ${BRAND.line}`,
                borderRadius: 2,
                p: 2,
              }}
            >
              <Skeleton variant="text" animation="wave" width="55%" height={28} sx={{ mb: 1 }} />
              <Skeleton variant="text" animation="wave" width="80%" height={22} />
              <Skeleton variant="text" animation="wave" width="68%" height={22} />
              <Skeleton variant="rounded" animation="wave" width="42%" height={28} sx={{ mt: 1 }} />
            </Box>
          ))}
        </Box>
      );
    }

    if (data.length === 0) {
      return (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {searchTerm ? noResultsMessage : emptyMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: { xs: 1.5, sm: 2 } }}>
        {data.map((row, index) => (
          <Box
            key={row.id || row.storeId || index}
            sx={{
              border: `1px solid ${BRAND.line}`,
              borderRadius: 2,
              p: { xs: 1.5, sm: 2 },
              bgcolor: BRAND.surface,
              minWidth: 0,
            }}
          >
            {primaryColumn ? (
              <Box sx={{ mb: detailColumns.length > 0 || hasInlineActions ? 1.25 : 0, minWidth: 0 }}>
                {renderCellContent(primaryColumn, row, index)}
              </Box>
            ) : null}

            {detailColumns.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {detailColumns.map((column, columnIndex) => (
                  <Box
                    key={column.field || columnIndex}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                        flexShrink: 0,
                        pt: 0.25,
                      }}
                    >
                      {column.header}
                    </Typography>
                    <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                      {renderCellContent(column, row, index)}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}

            {hasInlineActions ? (
              <Box
                sx={{
                  mt: detailColumns.length > 0 || primaryColumn ? 1.5 : 0,
                  pt: detailColumns.length > 0 || primaryColumn ? 1.25 : 0,
                  borderTop: detailColumns.length > 0 || primaryColumn ? `1px solid ${BRAND.lineSoft}` : 'none',
                }}
              >
                {renderRowActions(row, index)}
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>
    );
  };

  const renderDesktopTable = () => (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table sx={{ ...tableSx, minWidth: columns.length > 4 ? 640 : undefined }}>
        <TableHead sx={{ backgroundColor: BRAND.background }}>
          <TableRow sx={{ height: 48 }}>
            {columns.map((column, index) => (
              <TableCell 
                key={index} 
                align={column.align || 'left'}
                sx={{ 
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: BRAND.text,
                  whiteSpace: 'nowrap',
                }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={sortField === column.field}
                    direction={sortField === column.field ? sortDirection : 'asc'}
                    onClick={() => handleSort(column.field)}
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      '&:hover': {
                        color: BRAND.accentText,
                      },
                      '&.Mui-active': {
                        color: BRAND.accentText,
                      },
                      '& .MuiTableSortLabel-icon': {
                        fontSize: '1rem'
                      },
                      '&.Mui-active .MuiTableSortLabel-icon': {
                        color: BRAND.accentText,
                      }
                    }}
                  >
                    {column.header}
                  </TableSortLabel>
                ) : (
                  column.header
                )}
              </TableCell>
            ))}
            {actionMenuItems.length > 0 && (
              <TableCell 
                align="right"
                sx={{ 
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: BRAND.text,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('common.actions')}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
              <TableRow key={`loading-row-${rowIndex}`}>
                {columns.map((column, columnIndex) => (
                  <TableCell key={`${column.field || columnIndex}-loading-${rowIndex}`} align={column.align || 'left'}>
                    {renderLoadingCell(column, rowIndex, columnIndex)}
                  </TableCell>
                ))}
                {actionMenuItems.length > 0 && (
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Skeleton variant="circular" animation="wave" width={28} height={28} />
                      <Skeleton variant="circular" animation="wave" width={28} height={28} />
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + (actionMenuItems.length > 0 ? 1 : 0)} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  {searchTerm ? noResultsMessage : emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, index) => (
              <TableRow key={row.id || index} hover>
                {columns.map((column, colIndex) => (
                  <TableCell key={colIndex} align={column.align || 'left'}>
                    {renderCellContent(column, row, index)}
                  </TableCell>
                ))}
                {actionMenuItems.length > 0 && (
                  <TableCell align="right">
                    {actionMenuItems.map((action, actionIndex) => (
                      <Tooltip key={actionIndex} title={action.tooltip}>
                        <IconButton
                          onClick={(e) => action.onClick ? action.onClick(row, index) : handleMenuClick(e, row, index)}
                          size="small"
                          sx={{ color: action.color || BRAND.textMuted }}
                        >
                          {action.icon}
                        </IconButton>
                      </Tooltip>
                    ))}
                    {actionMenuItems.length > 1 && (
                      <Tooltip title={t('common.moreOptions')}>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, row, index)}
                          size="small"
                          sx={{ color: BRAND.textMuted }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Paper sx={{ borderRadius: BRAND_RADIUS.card, overflow: 'hidden', ...sx }}>
      {/* Search and Controls */}
      {(searchable || onRefresh) && (
        <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: `1px solid ${BRAND.line}`, bgcolor: BRAND.background }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
            {searchable && (
              <TextField
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => onSearchChange?.(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  minWidth: { xs: 0, sm: 250, md: 300 },
                  width: { xs: '100%', sm: 'auto' },
                  flex: { sm: 1 },
                }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
              {filterable && (
                <Button
                  startIcon={activeFilterCount > 0 ? <FilterAltIcon /> : <FilterIcon />}
                  variant="outlined"
                  onClick={handleFilterClick}
                  sx={{ 
                    borderColor: BRAND.accent,
                    color: BRAND.accentText,
                    backgroundColor: activeFilterCount > 0 ? brandAlpha('accent', 0.1) : 'transparent',
                    flex: { xs: 1, sm: '0 0 auto' },
                  }}
                >
                  {t('common.filters')}
                  {activeFilterCount > 0 && (
                    <Chip
                      label={activeFilterCount}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        minWidth: 20,
                        backgroundColor: BRAND.accent,
                        color: BRAND.onAccent,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                </Button>
              )}
              {onRefresh && (
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={onRefresh}
                  variant="outlined"
                  sx={{
                    borderColor: BRAND.line,
                    color: BRAND.text,
                    flex: { xs: 1, sm: '0 0 auto' },
                    '&:hover': {
                      borderColor: BRAND.accent,
                      backgroundColor: brandAlpha('accent', 0.04),
                    },
                  }}
                >
                  {t('common.refresh')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {isMobile ? renderMobileCards() : renderDesktopTable()}

      {/* Pagination */}
      {pagination && (
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          labelRowsPerPage={isMobile ? '' : t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} ${t('common.of')} ${count}`
          }
          sx={{
            borderTop: `1px solid ${BRAND.line}`,
            overflow: 'hidden',
            '& .MuiTablePagination-toolbar': {
              paddingLeft: { xs: 1, sm: 2 },
              paddingRight: { xs: 1, sm: 2 },
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              minHeight: { xs: 56, sm: 52 },
              gap: { xs: 0.5, sm: 0 },
            },
            '& .MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'block' },
            },
            '& .MuiTablePagination-selectLabel': {
              display: { xs: 'none', sm: 'block' },
              fontWeight: 600,
              color: BRAND.text
            },
            '& .MuiTablePagination-input': {
              display: { xs: 'none', sm: 'inline-flex' },
            },
            '& .MuiTablePagination-displayedRows': {
              fontWeight: 600,
              color: BRAND.text,
              margin: 0,
            }
          }}
        />
      )}

      {/* Action Menu */}
      {actionMenuItems.length > 1 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 160,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 2
            }
          }}
        >
          {actionMenuItems.map((action, index) => (
            <MenuItem key={index} onClick={() => handleActionClick(action)}>
              {action.icon && <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{action.icon}</Box>}
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Filter Dialog */}
      {filterable && (
        <ScrollableDialog
          open={filterDialogOpen}
          onClose={handleFilterClose}
          maxWidth="sm"
          paperSx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
          title={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterAltIcon sx={{ color: BRAND.accentText }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('common.filters')}</Typography>
            </Box>
          )}
          titleSx={{ borderBottom: `1px solid ${BRAND.line}`, pb: 2 }}
          contentSx={{ pt: 3 }}
          actionsSx={{ p: 3, gap: 1 }}
          actions={(
            <>
              <Button
                onClick={handleFilterClear}
                startIcon={<ClearIcon />}
                color="error"
                variant="outlined"
              >
                {t('common.clearAll')}
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button onClick={handleFilterClose}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleFilterApply}
                variant="contained"
                startIcon={<FilterIcon />}
              >
                {t('common.applyFilters')}
              </Button>
            </>
          )}
        >
          {filterOptions.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('common.noFiltersAvailable')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {filterOptions.map((filter) => (
                <Box key={filter.key}>
                  {filter.type === 'select' && (
                    <FormControl fullWidth>
                      <InputLabel>{filter.label}</InputLabel>
                      <Select
                        value={tempFilters[filter.key] || ''}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                        label={filter.label}
                        MenuProps={{ disableScrollLock: true }}
                      >
                        <MenuItem value="">
                          <em>{t('common.all')}</em>
                        </MenuItem>
                        {filter.options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {filter.type === 'checkbox' && (
                    <FormControl component="fieldset">
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {filter.label}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {filter.options.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            control={
                              <Checkbox
                                checked={(tempFilters[filter.key] || []).includes(option.value)}
                                onChange={(e) => {
                                  const currentValues = tempFilters[filter.key] || [];
                                  const newValues = e.target.checked
                                    ? [...currentValues, option.value]
                                    : currentValues.filter(v => v !== option.value);
                                  handleFilterChange(filter.key, newValues);
                                }}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </Box>
                    </FormControl>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </ScrollableDialog>
      )}
    </Paper>
  );
};

export default DataTable;
